import React from 'react';
import { createPortal } from 'react-dom';
import { useSmartTooltipPosition } from '../../hooks/useSmartTooltipPosition';
import { getScoreColor } from '../../../config/scoring';
import { useUnitPreferences, formatWeight, formatWater } from '../../utils/unitConversions';
import { getDailyMetrics, formatDailyDate } from './dailyMetrics';

interface GoalTarget {
  value: number;
  type: 'over' | 'under' | 'target' | 'boolean';
  variance?: number;
}

interface DailyHealthData {
  date: string;
  actual_values?: {
    weight?: number;
    steps?: number;
    calories?: number;
    sleep_hours?: number;
    water_intake?: number;
    energy_level?: number;
    stress_level?: number;
  };
  // Legacy flat structure (for backwards compatibility)
  weight?: number;
  steps?: number;
  calories?: number;
  sleep?: number;
  water?: number;
  workouts?: number;
  energy_level?: number;
  stress_level?: number;
  notes?: string;
  calculated_score?: number;
  score?: number;
  status?: string;
  targets_at_time?: {
    steps?: GoalTarget;
    water_intake?: GoalTarget;
    sleep_hours?: GoalTarget;
    calories?: GoalTarget;
  };
  active_goals_snapshot?: string[]; // Array of goal template IDs (e.g., ['daily_calories', 'daily_steps'])
  // PHASE 5E: Tier status and soft score
  tierStatus?: 'tier3' | 'tier2' | 'tier1' | 'none';
  softScore?: number | null;
}

interface CalendarDayTooltipProps {
  data: DailyHealthData;
  visible: boolean;
  x: number;
  y: number;
  showAllMetrics?: boolean; // PHASE 5E: Show all captured metrics regardless of active goals (both client and coach views)
  hideMatchingSoftScore?: boolean; // PHASE 5E: Hide soft score when it matches hard score
  userGoals?: any; // PHASE 5E: User goals for tier-based coloring
  isCoachView?: boolean; // PHASE 5E: Explicit indicator that coach is viewing client data (must see all goal metrics including T1)
}

export const CalendarDayTooltip: React.FC<CalendarDayTooltipProps> = ({
  data,
  visible,
  x,
  y,
  showAllMetrics = false,
  hideMatchingSoftScore = false,
  userGoals = null,
  isCoachView = false
}) => {
  // Get user's unit preferences for display formatting
  const prefs = useUnitPreferences();

  if (!visible || !data) return null;

  // Derivation logic shared with the mobile DailyCard (extracted 2026-05-31)
  const { getMetricValue, getGoalTierColor, getSortedMetrics, isGoalActive } = getDailyMetrics(data, {
    showAllMetrics,
    userGoals,
    isCoachView,
  });

  const formatDate = formatDailyDate;

  // Use smart tooltip positioning hook
  // 'auto' will render to left side when near right edge of viewport
  const tooltipPosition = useSmartTooltipPosition(x, y, {
    estimatedHeight: 250,
    estimatedWidth: 250,
    side: 'auto'
  });

  const tooltipElement = (
    <div
      style={{
        ...tooltipPosition,
        backgroundColor: '#1e1b3a', // Subdued dark purple like Epic cards
        border: '1px solid var(--border-color)',
        borderRadius: 'var(--radius-medium)',
        padding: '12px',
        paddingBottom: '50px', // Extra padding at bottom for score display
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        minWidth: '200px',
        maxWidth: '250px',
        fontSize: '12px',
        color: '#ffffff' // Always white text for purple background
      }}
    >
      {/* Header */}
      <div style={{
        fontWeight: '600',
        marginBottom: '8px',
        fontSize: '13px',
        color: '#ffffff' // White header text
      }}>
        {formatDate(data.date)}
      </div>

      {/* PHASE 5E: Metrics with Progress Bars - Dynamically sorted by tier + weight */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        marginBottom: '8px'
      }}>
        {getSortedMetrics().map((metric) => {
          const value = getMetricValue(metric.dataField)!;
          const targetData = metric.targetField ? data.targets_at_time?.[metric.targetField as keyof typeof data.targets_at_time] : null;
          const target = (targetData as any)?.goal_value || (targetData as any)?.value || metric.defaultTarget;
          const tierColor = getGoalTierColor(metric.templateId);

          // Get target type and variance for calories (if applicable)
          const targetType = (targetData as any)?.goal_type || (targetData as any)?.type || 'target';
          const variance = (targetData as any)?.variance_percent || (targetData as any)?.variance || 10;

          return (
            <div key={metric.templateId}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span>{metric.icon}</span>
                  <span style={{
                    fontSize: '10px',
                    color: tierColor,
                    fontWeight: tierColor !== '#d1d5db' ? '700' : '400'
                  }}>
                    {metric.displayName}
                  </span>
                </div>
                <span style={{ fontSize: '10px', fontWeight: '500', color: '#ffffff' }}>
                  {metric.templateId === 'daily_water'
                    ? `${formatWater(value, prefs.units.water)} / ${formatWater(target, prefs.units.water)}`
                    : metric.unit
                      ? `${value ?? 0}${metric.unit} / ${target ?? 0}${metric.unit}`
                      : `${(value ?? 0).toLocaleString()} / ${(target ?? 0).toLocaleString()}`
                  }
                </span>
              </div>
              {metric.renderProgressBar(value, target, targetType, variance)}
            </div>
          );
        })}
      </div>

      {/* Divider Line */}
      <div style={{
        borderTop: '1px solid rgba(255, 255, 255, 0.15)',
        marginTop: '8px',
        marginBottom: '8px'
      }} />

      {/* Additional Tracking Metrics (below divider) */}

      {/* Weight - Always shown if available (core metric) */}
      {(() => {
        const weight = getMetricValue('weight');
        return weight ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            marginBottom: '4px'
          }}>
            <span>⚖️</span>
            <span style={{ fontSize: '11px', color: '#d1d5db' }}>
              Weight: {formatWeight(weight, prefs.units.weight)}
            </span>
          </div>
        ) : null;
      })()}

      {/* Workouts (if any) */}
      {data.workouts > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          marginBottom: '4px'
        }}>
          <span>💪</span>
          <span style={{ fontSize: '11px', color: '#d1d5db' }}>
            {data.workouts} workout{data.workouts > 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Energy Level - Only if tracking goal enabled AND has value */}
      {isGoalActive('daily_energy') && data.energy_level !== null && data.energy_level !== undefined && (() => {
        const tierColor = getGoalTierColor('daily_energy');
        return (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            marginBottom: '4px'
          }}>
            <span>⚡</span>
            <span style={{
              fontSize: '11px',
              color: tierColor,
              fontWeight: tierColor !== '#d1d5db' ? '700' : '400'
            }}>
              Energy: {data.energy_level}/10
            </span>
          </div>
        );
      })()}

      {/* Stress Level - Only if tracking goal enabled AND has value */}
      {isGoalActive('daily_stress') && data.stress_level !== null && data.stress_level !== undefined && (() => {
        const tierColor = getGoalTierColor('daily_stress');
        return (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            marginBottom: '4px'
          }}>
            <span>😰</span>
            <span style={{
              fontSize: '11px',
              color: tierColor,
              fontWeight: tierColor !== '#d1d5db' ? '700' : '400'
            }}>
              Stress: {data.stress_level}/10
            </span>
          </div>
        );
      })()}

      {/* Notes */}
      {data.notes && (
        <div style={{
          marginTop: '8px',
          padding: '6px',
          backgroundColor: 'rgba(255, 255, 255, 0.1)', // Light overlay for notes section
          borderRadius: 'var(--radius-small)',
          borderLeft: '3px solid #60a5fa' // Fixed blue border
        }}>
          <div style={{
            fontSize: '10px',
            color: '#d1d5db',
            marginBottom: '2px'
          }}>
            Notes:
          </div>
          <div style={{
            fontSize: '11px',
            color: '#ffffff',
            fontStyle: 'italic'
          }}>
            "{data.notes}"
          </div>
        </div>
      )}

      {/* PHASE 5E: Score Display with Real + Soft Scores */}
      {(() => {
        const realScore = data.score;
        const softScore = data.softScore;
        const tierStatus = data.tierStatus;

        // Determine what to display based on tier status
        let scoreDisplay: React.ReactNode = '';
        let displayScore = 0; // Track the actual numeric score value

        if (tierStatus === 'tier3' && realScore !== null && realScore !== undefined) {
          // Tier 3: Show real score with soft score in parentheses (no % symbol)
          // Conditionally hide soft score if it matches real score (based on user preference)
          displayScore = Math.round(realScore);
          const roundedSoftScore = softScore !== null && softScore !== undefined ? Math.round(softScore) : null;

          // Build colored score display using JSX
          const realScoreColor = getScoreColor(displayScore);
          const softScoreColor = roundedSoftScore !== null ? getScoreColor(roundedSoftScore) : null;

          // PHASE 5E: Hide soft score if toggle enabled and scores match
          const shouldShowSoftScore = roundedSoftScore !== null &&
            !(hideMatchingSoftScore && displayScore === roundedSoftScore);

          scoreDisplay = (
            <>
              <span style={{ color: realScoreColor, fontWeight: 'bold' }}>{displayScore}</span>
              {shouldShowSoftScore && (
                <>
                  {' ('}
                  <span style={{ color: softScoreColor!, fontWeight: 'bold' }}>{roundedSoftScore}</span>
                  {')'}
                </>
              )}
            </>
          );
        } else if (tierStatus === 'tier2') {
          // Tier 2: Show soft score in parentheses (practice mode, no % symbol)
          // Fall back to realScore if softScore not available
          const score = softScore !== null && softScore !== undefined ? softScore : realScore;
          if (score !== null && score !== undefined) {
            displayScore = Math.round(score);
            const scoreColor = getScoreColor(displayScore);
            scoreDisplay = (
              <>
                {'('}
                <span style={{ color: scoreColor, fontWeight: 'bold' }}>{displayScore}</span>
                {')'}
              </>
            );
          }
        } else if (tierStatus === 'tier1') {
          // Tier 1: Show soft score in parentheses (coach-only tracking, no % symbol)
          // Fall back to realScore if softScore not available
          const score = softScore !== null && softScore !== undefined ? softScore : realScore;
          if (score !== null && score !== undefined) {
            displayScore = Math.round(score);
            const scoreColor = getScoreColor(displayScore);
            scoreDisplay = (
              <>
                {'('}
                <span style={{ color: scoreColor, fontWeight: 'bold' }}>{displayScore}</span>
                {')'}
              </>
            );
          }
        } else if (tierStatus === 'none' && (realScore !== null && realScore !== undefined)) {
          // No tier: Show real score if available (legacy behavior, no % symbol)
          displayScore = Math.round(realScore);
          const scoreColor = getScoreColor(displayScore);
          scoreDisplay = (
            <span style={{ color: scoreColor, fontWeight: 'bold' }}>{displayScore}</span>
          );
        }

        console.log('[Tooltip Score Display]', { scoreDisplay, displayScore });

        // Hide score badge if score is 0 (keeps tile clean when no goals are being scored)
        return scoreDisplay && displayScore > 0 ? (
          <div style={{
            position: 'absolute',
            bottom: '12px',
            right: '12px',
            fontSize: '28px',
            fontWeight: '700',
            color: 'rgba(255, 255, 255, 0.9)',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
            zIndex: 10,
            pointerEvents: 'none',
            lineHeight: '1',
            userSelect: 'none'
          }}>
            {scoreDisplay}
          </div>
        ) : null;
      })()}

      {/* Tooltip Arrow */}
      <div style={{
        position: 'absolute',
        bottom: '-6px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 0,
        height: 0,
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        borderTop: '6px solid var(--bg-primary)'
      }} />
    </div>
  );

  // Use portal to render at document body level to avoid positioning context issues
  return createPortal(tooltipElement, document.body);
};